import React from 'react';
import { FaSquare, FaCheckSquare, FaMinusSquare } from 'react-icons/fa';
import { IoMdArrowDropright } from 'react-icons/io';
import TreeView, { flattenTree } from 'react-accessible-treeview';
import cx from 'classnames';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setTreeViewSelectedValue } from '@store/reducers/userSlice';

interface ArrowIconProps {
    isOpen: boolean;
    className?: string;
}

interface CheckBoxIconProps {
    variant: string;
    [key: string]: any;
}

const dataWithPredefinedIds = flattenTree({
    name: '',
    id: 890,
    children: [
        {
            id: 'data-string',
            name: 'Fruits',
            children: [
                { name: 'Avocados', id: 690 },
                { name: 'Bananas', id: 1001 },
                { name: 'Berries', id: 793 },
                { name: 'Oranges', id: 18 },
                { name: 'Pears', id: 9990 },
            ],
        },
        {
            id: 'one',
            name: 'Drinks',
            children: [
                { name: 'Apple Juice', id: 7 },
                { name: 'Chocolate', id: 12 },
                { name: 'Coffee', id: 1 },
                {
                    id: 908,
                    name: 'Tea',
                    children: [
                        { name: 'Black Tea', id: 923 },
                        { name: 'Green Tea', id: 43 },
                        { name: 'Red Tea', id: 23 },
                        { name: 'Matcha', id: 4 },
                    ],
                },
            ],
        },
        {
            id: 42,
            name: 'Vegetables',
            children: [
                { name: 'Beets', id: 672 },
                { name: 'Carrots', id: 13 },
                { name: 'Celery', id: 123 },
                { name: 'Lettuce', id: 893 },
                { name: 'Onions', id: 82 },
            ],
        },
    ],
});

function MyTreeView({ data, isDisabled }: { data: any; isDisabled: boolean }) {
    const treeData = flattenTree({
        name: '',
        id: 890,
        children: data,
    });

    const selectedId = useAppSelector(
        (state) => state.user.treeViewSelectedValue
    );

    const findParentIds = (nodes: any[], targetId: any): any[] => {
        // console.log('nodes', nodes);
        // console.log('targetId', targetId);
        const parentIds: any[] = [];

        const findNodeAndParents = (nodes: any[], targetId: any): boolean => {
            for (const node of nodes) {
                if (node.id === targetId) {
                    // console.log('node.id', node.id);
                    // console.log('node', node);

                    let currentNode = node;
                    while (currentNode.parent && currentNode.parent !== 890) {
                        parentIds.push(currentNode.parent);
                        currentNode = nodes.find(
                            (n) => n.id === currentNode.parent
                        );
                    }
                    return true;
                }

                if (node.children) {
                    const found = findNodeAndParents(node.children, targetId);
                    if (found) {
                        return true;
                    }
                }
            }
            return false;
        };

        findNodeAndParents(nodes, targetId);
        return parentIds.reverse();
    };

    const expandedIds = findParentIds(treeData, selectedId);

    return (
        <div>
            <div className="checkbox">
                <TreeViewComponent
                    data={treeData}
                    selectedId={selectedId}
                    expandedIds={expandedIds}
                    isDisabled={isDisabled}
                />
            </div>
        </div>
    );
}

function TreeViewComponent({
    data,
    selectedId,
    expandedIds,
    isDisabled,
}: {
    data: any;
    selectedId: any;
    expandedIds: any[];
    isDisabled: boolean;
}) {
    const dispatch = useAppDispatch();

    const handleSelectId = (id: any) => {
        if (isDisabled) return;
        dispatch(setTreeViewSelectedValue(id));
    };

    return (
        <TreeView
            data={data}
            aria-label="Single select"
            multiSelect={false}
            expandedIds={expandedIds}
            propagateSelect
            propagateSelectUpwards
            togglableSelect
            nodeRenderer={({
                element,
                isBranch,
                isExpanded,
                isSelected,
                isHalfSelected,
                getNodeProps,
                level,
                handleSelect,
                handleExpand,
            }) => {
                const isChecked = selectedId === element.id;
                return (
                    <div
                        {...getNodeProps({ onClick: handleExpand })}
                        style={{ marginLeft: 40 * (level - 1) }}
                    >
                        {isBranch && (
                            <ArrowIcon
                                className="arrow-icon-class"
                                isOpen={isExpanded}
                            />
                        )}
                        <CheckBoxIcon
                            className="checkbox-icon"
                            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                if (!isDisabled) {
                                    handleSelect(e);
                                    e.stopPropagation();
                                    handleSelectId(element.id);
                                }
                            }}
                            variant={
                                isHalfSelected
                                    ? 'some'
                                    : isChecked
                                      ? 'all'
                                      : 'none'
                            }
                            style={{
                                opacity: isDisabled ? 0.5 : 1,
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                pointerEvents: isDisabled ? 'none' : 'auto',
                            }}
                        />
                        <span className="name">{element.name}</span>
                    </div>
                );
            }}
        />
    );
}

const ArrowIcon: React.FC<ArrowIconProps> = ({ isOpen, className }) => {
    const baseClass = 'arrow';
    const classes = cx(
        baseClass,
        { [`${baseClass}--closed`]: !isOpen },
        { [`${baseClass}--open`]: isOpen },
        className
    );
    return <IoMdArrowDropright className={classes} />;
};

const CheckBoxIcon: React.FC<CheckBoxIconProps> = ({ variant, ...rest }) => {
    switch (variant) {
        case 'all':
            return <FaCheckSquare {...rest} />;
        case 'none':
            return <FaSquare {...rest} />;
        case 'some':
            return <FaMinusSquare {...rest} />;
        default:
            return null;
    }
};

export default MyTreeView;
